import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, FileCheck2, ExternalLink, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { safeHtml } from '../lib/sanitize';
import PageHeader from '../components/PageHeader';
import useTheme4A from '../components/useTheme4A';
import useAllowPrint from '../components/useAllowPrint';

const STATUS_STYLE = {
  consenting: 'bg-stone-100 text-stone-700',
  generating: 'bg-blue-100 text-blue-800',
  awaiting_review: 'bg-amber-100 text-amber-800',
  released: 'bg-emerald-100 text-emerald-800',
  revoked: 'bg-rose-100 text-rose-700',
};

const STATUS_COPY = {
  consenting: 'Awaiting generation',
  generating: 'Being prepared',
  awaiting_review: 'Ready for your review',
  released: 'Released to recipient',
  revoked: 'Revoked by you',
};

export default function MyReportsPage() {
  useTheme4A();
  useAllowPrint();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);  // consent object for the open modal

  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/mine`);
    if (r.ok) setReports((await r.json()).reports || []);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openReview = async (consent_id) => {
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/consent/${consent_id}`);
    if (r.ok) setReviewing(await r.json());
  };

  const submitAction = async (action) => {
    if (!reviewing) return;
    const note = action === 'revoke' || action === 'request_changes'
      ? window.prompt(`${action === 'revoke' ? 'Reason for revoking' : 'What changes do you want'}?`)
      : null;
    if ((action === 'revoke' || action === 'request_changes') && !note) return;
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${reviewing.consent_id}/review`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, note }),
    });
    if (r.ok) {
      setReviewing(null);
      refresh();
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="my-reports-page">
      <PageHeader title="My Reports" backTo="/dashboard" icon={FileCheck2} />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
          <h2 className="text-lg font-semibold text-stone-900">Third-party reports</h2>
          <p className="text-sm text-stone-600 mt-1">Reports generated about your programme engagement for courts, clinicians, sponsors, employers or insurance.  Each report is gated by your explicit consent and you can review or revoke before it is released.</p>

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin inline text-stone-500" /></div>
          ) : reports.length === 0 ? (
            <div className="text-center py-10 text-stone-500 text-sm" data-testid="no-reports">No reports yet.  Reports are initiated by your Axiom administrator and you'll be asked to consent before any are generated.</div>
          ) : (
            <ul className="divide-y divide-stone-100 mt-3">
              {reports.map(r => (
                <li key={r.consent_id} className="py-3" data-testid={`report-row-${r.consent_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-stone-900">{r.report_type_label}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        For <b>{r.recipient.name}</b>{r.recipient.organisation ? ` · ${r.recipient.organisation}` : ''}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">Ref <span className="font-mono">{r.report_ref}</span></div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLE[r.status] || 'bg-stone-100 text-stone-700'}`}>
                          {STATUS_COPY[r.status] || r.status}
                        </span>
                      </div>
                    </div>
                    {r.status === 'awaiting_review' && (
                      <button data-testid={`review-btn-${r.consent_id}`}
                        onClick={() => openReview(r.consent_id)}
                        className="px-3 py-1.5 bg-stone-900 text-white rounded-full text-xs font-medium hover:bg-stone-800 shrink-0">
                        Review now
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="text-xs text-stone-500 px-2">
          <Shield className="w-3 h-3 inline mr-1" /> Anyone you share a report with can verify its authenticity at <code className="font-mono">/verify/&lt;reference&gt;</code>: content is never disclosed there.
        </div>
      </div>

      {reviewing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setReviewing(null)} data-testid="review-modal">
          <div className="bg-white rounded-2xl p-5 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-stone-200 pb-3">
              <div>
                <h3 className="text-base font-semibold text-stone-900">{reviewing.report_type_label}</h3>
                <p className="text-xs text-stone-500">Ref <span className="font-mono">{reviewing.report_ref}</span> · for <b>{reviewing.recipient.name}</b></p>
              </div>
              <button onClick={() => setReviewing(null)} className="text-stone-400 hover:text-stone-700 text-lg">×</button>
            </div>
          <div className="flex-1 overflow-y-auto py-4">
            {reviewing.status === 'awaiting_review' && (
              <div className="mb-3 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900" data-testid="draft-pending-banner">
                DRAFT: PENDING PROFESSIONAL REVIEW
              </div>
            )}
            <div className="prose prose-sm prose-stone max-w-none" dangerouslySetInnerHTML={safeHtml(reviewing.content_html || '<p class="text-stone-400">No content yet.</p>')} />
          </div>
            {reviewing.content_pdf_base64 && (
              <div className="mt-2">
                <a href={reviewing.content_pdf_base64} download={`${reviewing.report_ref}.pdf`}
                  className="inline-flex items-center gap-1 text-xs text-stone-700 underline hover:text-stone-900"
                  data-testid="download-draft-pdf">
                  <ExternalLink className="w-3 h-3" /> Download draft PDF
                </a>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-stone-200">
              <button data-testid="approve-release-btn" onClick={() => submitAction('approve')}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-medium hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> Approve &amp; release
              </button>
              <button data-testid="request-changes-btn" onClick={() => submitAction('request_changes')}
                className="px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium hover:bg-amber-200">
                Request changes
              </button>
              <button data-testid="revoke-btn" onClick={() => submitAction('revoke')}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-100 text-rose-700 rounded-full text-sm font-medium hover:bg-rose-200">
                <AlertTriangle className="w-4 h-4" /> Revoke consent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
